import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../api-response';
import { BaseApiService } from '../../../core/base-api.service';
import { CreateFeedFileRequest, FeedFile, FeedFileDownload, UpdateFeedFileRequest } from '../models/provider.model';

/**
 * A file-based feed's named files — /api/v1/admin/feeds/{feedId}/files.
 * Super-admin only, and only for feeds whose provider has `acceptsFiles`
 * (core 400s otherwise). Error responses (400/404/409/503) carry a readable
 * `message` — show it (see `apiErrorMessage`); a 413 from the HTTP layer may
 * have no body.
 */
@Injectable({ providedIn: 'root' })
export class FeedFileService extends BaseApiService {
  private readonly http = inject(HttpClient);

  private filesUrl(feedId: string): string {
    return `${this.coreApiUrl}/api/v1/admin/feeds/${feedId}/files`;
  }

  /** Sorted by filename. */
  getAll(feedId: string): Observable<ApiResponse<FeedFile[]>> {
    return this.http.get<ApiResponse<FeedFile[]>>(this.filesUrl(feedId));
  }

  get(feedId: string, fileId: string): Observable<ApiResponse<FeedFile>> {
    return this.http.get<ApiResponse<FeedFile>>(`${this.filesUrl(feedId)}/${fileId}`);
  }

  /** 409 if the filename is already used in this feed. */
  create(feedId: string, req: CreateFeedFileRequest): Observable<ApiResponse<FeedFile>> {
    return this.http.post<ApiResponse<FeedFile>>(this.filesUrl(feedId), req);
  }

  update(feedId: string, fileId: string, req: UpdateFeedFileRequest): Observable<ApiResponse<FeedFile>> {
    return this.http.put<ApiResponse<FeedFile>>(`${this.filesUrl(feedId)}/${fileId}`, req);
  }

  /** Delivered file sets stay intact. */
  delete(feedId: string, fileId: string): Observable<void> {
    return this.http.delete<void>(`${this.filesUrl(feedId)}/${fileId}`);
  }

  /** Replaces the content and switches the file to MANUAL (URL settings are
   *  kept for later). 413 over 100 MB. */
  uploadContent(feedId: string, fileId: string, file: File, metadata?: Record<string, string>): Observable<ApiResponse<FeedFile>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (metadata && Object.keys(metadata).length) formData.append('metadata', JSON.stringify(metadata));
    return this.http.put<ApiResponse<FeedFile>>(`${this.filesUrl(feedId)}/${fileId}/content`, formData);
  }

  /** 202: the check runs shortly after — poll `get` until `check.lastCheckAt`
   *  moves past the click. 409 if the file is MANUAL. */
  checkNow(feedId: string, fileId: string): Observable<ApiResponse<FeedFile>> {
    return this.http.post<ApiResponse<FeedFile>>(`${this.filesUrl(feedId)}/${fileId}/check-now`, null);
  }

  /** 15-minute link to the current content; 404 if there's none yet. Fetch on click. */
  getDownloadUrl(feedId: string, fileId: string): Observable<ApiResponse<FeedFileDownload>> {
    return this.http.get<ApiResponse<FeedFileDownload>>(`${this.filesUrl(feedId)}/${fileId}/download-url`);
  }
}
